import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";

import { rootReducer } from "fond/store";

import { headersCreator } from "./index";

export type RootState = ReturnType<typeof rootReducer>;

export const fondServiceURL = `${import.meta.env.REACT_APP_FOND_SERVICE_DOMAIN}`;

/**
 * Custom backoff strategy with delays of 0.5s, 2s, 5s, 10s, and 30s
 */
const backoffDelays = [0, 500, 2_000, 5_000, 10_000, 30_000];
const customBackOff = async (attempt = 0, maxRetries = 0) => {
  const delayIndex = Math.min(backoffDelays.length - 1, maxRetries, attempt);

  await new Promise((resolve) => {
    setTimeout(resolve, backoffDelays[delayIndex]);
  });
};

/**
 * A baseQuery with retry logic and exponential backoff
 */
const staggeredBaseQuery = retry(
  fetchBaseQuery({ baseUrl: fondServiceURL, prepareHeaders: (headers, api) => headersCreator.createHeaders(headers, api) }),
  {
    /* By default we do not retry, but you can set this to any number for a specific endpoint using the `extraOptions` field
     * For example:
     *
     * getClientSecret: build.query({
     *     query: () => ({
     *       url: "/blah",
     *     }),
     *     extraOptions: { maxRetries: 3 },
     *   }),
     */
    maxRetries: 0,
    backoff: customBackOff,
  }
);

// initialize an empty api service that we inject endpoints into
export const apiSlice = createApi({
  baseQuery: staggeredBaseQuery,
  endpoints: () => ({}),
  keepUnusedDataFor: 60,
  tagTypes: [
    "Account",
    "AccountModules",
    "AllAccountSubscriptions",
    "Architecture",
    "Banner",
    "BOM",
    "CustomWorkflowExecutions",
    "Draft",
    "Export",
    "Exports",
    "Feature",
    "FeatureTotals",
    "Folder",
    "Follow",
    "ImportStatus",
    "Invitations",
    "Layer",
    "LayerFeatures",
    "Layers",
    "Layout",
    "Layouts",
    "MapLayerConfig",
    "MentionSuggestions",
    "Modules",
    "MultiProject",
    "MultiProjectStatistics",
    "MultiReport",
    "Permission",
    "Permissions",
    "Project",
    "Projects",
    "RenewStatus",
    "Report",
    "ShareSuggestions",
    "Subscription",
    "Task",
    "User",
    "UserAccountAllocation",
    "UserPreference",
    "Users",
    "Version",
    "VersionRootConfiguration",
    "Versions",
    "VersionScreenshot",
    "VersionStatus",
    "VersionScenarioConfiguration",
  ],
});
